import { G } from '@mobily/ts-belt';
import stringify from 'safe-stable-stringify';

import { isMaturityCampaignType } from '@Common/bo/b2b_campaigns';
import { esContractTypes } from '@Common/types/b2b/contracts';
import { B2bCashSource } from '@Common/types/bo/b2b/b2b';
import { B2bCampaignType } from '@Common/types/bo/b2b/campaigns';
import {
  type ContractTypeDefinition,
  type FieldRule,
  getContractTypeDefinition,
} from '@Common/types/bo/b2b/contracts/contract_type_definitions';

import type {
  B2bCampaignActionHandler,
  B2bCampaignsProcessingOptionsByCashSources,
  B2bContractParticipationParameters,
  B2bContractProfitSharingParameters,
  B2bContractType,
} from '@Common/types/bo/b2b/contracts';

const INVALID_CET_ALLOCATION_COMPUTED_BY_ERROR =
  'CET campaigns cannot have allocation computed by distributor';

export function isERContract(contractType: B2bContractType | null): boolean {
  return G.isNotNullable(contractType) && getContractTypeDefinition(contractType).family === 'er';
}

export function isInvalidVoluntaryDepositContract(contract: {
  contract_type: B2bContractType;
}): boolean {
  return !esContractTypes.includes(contract.contract_type);
}

/**
 * An insurer's offers (AXA SOLES, Malakoff Humanis ESSENTIEL) cannot be created on another insurer's
 * environment, while Shares environments (neither AXA nor Malakoff) offer them all.
 */
export function isContractTypeAvailableOnEnvironment(
  contractType: B2bContractType,
  environment: { axa: boolean; mlk: boolean },
): boolean {
  const { restrictedToInsurer } = getContractTypeDefinition(contractType);
  if (restrictedToInsurer === 'axa') {
    return !environment.mlk;
  }
  if (restrictedToInsurer === 'malakoff') {
    return !environment.axa;
  }
  return true;
}

/**
 * Campaigns fully run either by the company or by the account keeper: allocation, choice
 * collection and payout are not split between both. Matching contribution computation is
 * configured separately.
 *
 * CET allocations are left out: the account keeper cannot compute them (BRAV-10530), so some
 * contract types always store them as computed by the company (see ContractTypeDefinition).
 */
export function hasSingleCampaignActionHandler(
  campaigns: B2bCampaignsProcessingOptionsByCashSources,
): boolean {
  return Object.entries(campaigns).every(
    ([cashSource, options]) =>
      G.isNullable(options) ||
      ((cashSource === 'unused_vacation_days' ||
        options.allocation_computed_by === options.choice_collected_by) &&
        options.choice_collected_by === options.payout_by),
  );
}

const breaksLockedValue = (
  rule: FieldRule<unknown> | undefined,
  value: unknown,
): rule is FieldRule<unknown> =>
  Boolean(rule?.locked) && value !== undefined && value !== rule?.default;

type ContractValues = {
  label?: string | null;
  is_liv?: boolean;
  total_contribution_ceiling?: number | null;
  relationship_type?: string;
  filib_url?: string | null;
  campaigns?: B2bCampaignsProcessingOptionsByCashSources;
};

type ContractRuleCheck = (params: {
  name: string;
  definition: ContractTypeDefinition;
  contract: ContractValues;
}) => string | null;

const CONTRACT_RULE_CHECKS: ContractRuleCheck[] = [
  ({ name, definition: { contract: rules }, contract }) =>
    breaksLockedValue(rules.label, contract.label)
      ? `${name} contracts must be labelled "${rules.label.default}"`
      : null,
  ({ name, definition: { contract: rules }, contract }) =>
    breaksLockedValue(rules.isLiv, contract.is_liv)
      ? `${name} contracts ${rules.isLiv.default ? 'must' : 'cannot'} be LIV`
      : null,
  ({ name, definition: { contract: rules }, contract }) => {
    if (!breaksLockedValue(rules.totalContributionCeiling, contract.total_contribution_ceiling)) {
      return null;
    }
    const ceiling = rules.totalContributionCeiling.default;
    return G.isNullable(ceiling)
      ? `${name} contracts cannot have a total contribution ceiling`
      : `${name} contracts must have a total contribution ceiling of ${ceiling}`;
  },
  ({ name, definition: { contract: rules }, contract }) =>
    breaksLockedValue(rules.relationshipType, contract.relationship_type)
      ? `${name} contracts must have a ${rules.relationshipType.default} relationship`
      : null,
  ({ name, definition: { contract: rules }, contract }) => {
    if (!breaksLockedValue(rules.filibUrl, contract.filib_url)) {
      return null;
    }
    const filibUrl = rules.filibUrl.default;
    return G.isNullable(filibUrl)
      ? `${name} contracts cannot have a Filib URL`
      : `${name} contracts must have the Filib URL ${filibUrl}`;
  },
  ({ name, definition: { campaigns }, contract }) =>
    campaigns.hasSingleHandler &&
    G.isNotNullable(contract.campaigns) &&
    !hasSingleCampaignActionHandler(contract.campaigns)
      ? `${name} campaigns must be allocated, collected and paid out by the same handler`
      : null,
];

/**
 * Errors for the values a contract creation or update would write, against the rules of its type.
 * Only the given fields are checked: updates leave the omitted ones unchanged.
 */
export function getContractDefinitionErrors(
  contractType: B2bContractType,
  contract: ContractValues,
): string[] {
  const definition = getContractTypeDefinition(contractType);
  const name = contractType.toUpperCase();
  return CONTRACT_RULE_CHECKS.map((check) => check({ name, definition, contract })).filter(
    G.isNotNullable,
  );
}

export function isAllCampaignsProcessingOptionsSimilar(
  campaigns: B2bCampaignsProcessingOptionsByCashSources,
) {
  const stringifiedParticipation = stringify(campaigns.participation);
  const stringifiedProfitSharing = stringify(campaigns.profit_sharing);
  const stringifiedValueSharing = stringify(campaigns.value_sharing_bonus);
  const stringifiedVoluntaryDeposit = stringify(campaigns.voluntary_deposit);
  const stringifiedPUnusedVacDays = stringify(campaigns.unused_vacation_days);

  return (
    stringifiedParticipation === stringifiedProfitSharing &&
    stringifiedProfitSharing === stringifiedValueSharing &&
    stringifiedValueSharing === stringifiedVoluntaryDeposit &&
    stringifiedVoluntaryDeposit === stringifiedPUnusedVacDays
  );
}

export function isInvalidUnusedVacationDaysAllocationComputedBy(
  allocationComputedBy: B2bCampaignActionHandler | null | undefined,
): boolean {
  return allocationComputedBy === 'distributor';
}

export { INVALID_CET_ALLOCATION_COMPUTED_BY_ERROR };

const VALID_ALLOCATION_CHOICE_COMBINATIONS: [B2bCampaignActionHandler, B2bCampaignActionHandler][] =
  [
    ['company', 'company'],
    ['company', 'distributor'],
    ['distributor', 'distributor'],
  ];

export const INVALID_ALLOCATION_CHOICE_COMBINATION_ERROR =
  'Invalid combination of allocation computed by / choice collected by';

// Only company/company, company/distributor, and distributor/distributor are valid pairings of
// allocation_computed_by and choice_collected_by; distributor/company is not offered.
export function isValidAllocationChoiceCombination(
  allocationComputedBy: B2bCampaignActionHandler | null | undefined,
  choiceCollectedBy: B2bCampaignActionHandler | null | undefined,
): boolean {
  if (G.isNullable(allocationComputedBy) || G.isNullable(choiceCollectedBy)) {
    return true;
  }
  return VALID_ALLOCATION_CHOICE_COMBINATIONS.some(
    ([allocation, choice]) => allocation === allocationComputedBy && choice === choiceCollectedBy,
  );
}

/**
 * choice_collected_by and payout_by can be overridden manually by a BO Internal agent regardless of
 * what the selected contracts carry, so mismatches on those are only blocking on BO External.
 * allocation_computed_by can only be overridden from 'distributor' to 'company', never the other
 * way, so a mismatch on it can still force an unauthorized override and must be checked, unless the
 * campaign itself is set to 'company': every contract is then computed by the company anyway.
 * matching_contribution_computed_by is never editable from BO Internal (and not at all from BO
 * External), so it must always match across the selected contracts too.
 */
export function validateCampaignsProcessingOptionsOfContracts(
  campaignType: B2bCampaignType,
  contractsProcessingOptions: B2bCampaignsProcessingOptionsByCashSources[],
  {
    checkChoiceAndPayout = true,
    campaignAllocationComputedBy,
  }: {
    checkChoiceAndPayout?: boolean;
    campaignAllocationComputedBy?: B2bCampaignActionHandler | null;
  } = {},
): boolean {
  if (contractsProcessingOptions.length < 2) {
    return true;
  }

  const skipAllocation = campaignAllocationComputedBy === 'company';

  const relevantProcessingOptions = (
    contractProcessingOptions: B2bCampaignsProcessingOptionsByCashSources,
  ): Record<string, unknown> => {
    const processingOptions = getProcessingOptionsFromContract(
      campaignType,
      contractProcessingOptions,
    );

    return {
      ...(skipAllocation
        ? {}
        : { allocation_computed_by: processingOptions.allocation_computed_by }),
      ...(checkChoiceAndPayout
        ? {
            choice_collected_by: processingOptions.choice_collected_by,
            payout_by: processingOptions.payout_by,
          }
        : {}),
      matching_contribution_computed_by: processingOptions.matching_contribution_computed_by,
    };
  };

  const [firstContractProcessingOptions] = contractsProcessingOptions;

  const stringifiedProcessingOptions = JSON.stringify(
    relevantProcessingOptions(firstContractProcessingOptions),
  );

  return contractsProcessingOptions.every(
    (contractProcessingOptions) =>
      stringifiedProcessingOptions ===
      JSON.stringify(relevantProcessingOptions(contractProcessingOptions)),
  );
}

function getProcessingOptionsFromContract(
  campaignType: B2bCampaignType,
  contractProcessingOptions: B2bCampaignsProcessingOptionsByCashSources,
) {
  if (isMaturityCampaignType(campaignType)) {
    return {
      allocation_computed_by: 'company',
      choice_collected_by: 'distributor',
      payout_by: 'distributor',
      matching_contribution_computed_by: 'distributor',
    };
  }

  if (campaignType === 'matching_contribution') {
    return {
      allocation_computed_by: 'company',
      choice_collected_by: 'company',
      payout_by: 'company',
      matching_contribution_computed_by: 'company',
    };
  }

  const processingOptions: {
    allocation_computed_by?: B2bCampaignActionHandler;
    choice_collected_by?: B2bCampaignActionHandler;
    payout_by?: B2bCampaignActionHandler;
    matching_contribution_computed_by?: B2bCampaignActionHandler | null;
  } = {
    allocation_computed_by:
      contractProcessingOptions[campaignType]?.allocation_computed_by ?? 'company',
    choice_collected_by: contractProcessingOptions[campaignType]?.choice_collected_by ?? 'company',
    payout_by: contractProcessingOptions[campaignType]?.payout_by ?? 'company',
    matching_contribution_computed_by:
      contractProcessingOptions[campaignType]?.matching_contribution_computed_by,
  };

  return processingOptions;
}

// Distribution rules only live on the participation and profit sharing agreements of a contract.
export function getCampaignAgreementCashSource(
  campaignType: B2bCampaignType | null | undefined,
): 'participation' | 'profit_sharing' | null {
  return campaignType === 'participation' || campaignType === 'profit_sharing'
    ? campaignType
    : null;
}

function getAgreementDistribution(
  agreement: B2bContractParticipationParameters | B2bContractProfitSharingParameters,
) {
  return {
    distribution_salary_pct: agreement?.distribution_salary_pct ?? null,
    distribution_tenure_pct: agreement?.distribution_tenure_pct ?? null,
    distribution_uniformity_pct: agreement?.distribution_uniformity_pct ?? null,
  };
}

/**
 * A campaign computes a single distribution for all the contracts it runs on, so the selected
 * contracts must share the same distribution rules on the campaign agreement.
 */
export function areContractsAgreementDistributionsSimilar(
  campaignType: B2bCampaignType | null | undefined,
  contracts: {
    contract_type: B2bContractType;
    participation: B2bContractParticipationParameters;
    profit_sharing: B2bContractProfitSharingParameters;
  }[],
): boolean {
  const cashSource = getCampaignAgreementCashSource(campaignType);

  if (G.isNullable(cashSource)) {
    return true;
  }

  // ER contracts hold no distribution rules, they are ignored unless they are the only ones selected
  const contractsToCompare = contracts.filter((contract) => !isERContract(contract.contract_type));

  if (contractsToCompare.length < 2) {
    return true;
  }

  const [firstContract] = contractsToCompare;
  const firstDistribution = stringify(getAgreementDistribution(firstContract[cashSource]));

  return contractsToCompare.every(
    (contract) => stringify(getAgreementDistribution(contract[cashSource])) === firstDistribution,
  );
}

export const getFirstContractIndex = (contractTypes: B2bContractType[] | null): number | null => {
  if (G.isNullable(contractTypes) || contractTypes.length === 0) {
    return null;
  }

  if (
    contractTypes.length === 1 ||
    contractTypes.every((contractType) => isERContract(contractType))
  ) {
    return 0;
  }

  return contractTypes.findIndex((contractType) => !isERContract(contractType));
};

export function mutualizeCashSources(plansCashSources: B2bCashSource[][]) {
  const allCashSources = plansCashSources.reduce(
    (total, cashSources) => total.concat(cashSources),
    [],
  );

  return [...new Set(allCashSources)];
}
